import { api } from './api';
import type { Product } from './products.service';

export interface Enrollment {
  id: string;
  userId: string;
  productId?: string;
  accessGroupId?: string;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'PENDING' | 'PAUSED';
  source?: string;
  progress: number;
  expirationDate?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  product?: Product;
  accessGroup?: {
    id: string;
    title: string;
    products?: Product[];
  };
}

export interface EnrollmentFilters {
  page?: number;
  limit?: number;
  status?: string;
  userId?: string;
  productId?: string;
  search?: string;
}

class EnrollmentsService {
  async getMyEnrollments(): Promise<Enrollment[]> {
    const { data } = await api.get('/enrollments/my-products');
    const enrollments = Array.isArray(data) ? data : (data.data || []);
    return enrollments;
  }

  async getMyProductsInProgress(): Promise<Enrollment[]> {
    const enrollments = await this.getMyEnrollments();
    return enrollments.filter((e: Enrollment) => e.status === 'ACTIVE');
  }

  async getMyCompletedProducts(): Promise<Enrollment[]> {
    const enrollments = await this.getMyEnrollments();
    return enrollments.filter((e: Enrollment) => e.progress === 100);
  }

  async checkAccess(productId: string): Promise<{ hasAccess: boolean }> {
    const { data } = await api.get(`/enrollments/check-access/${productId}`);
    return data.data || data;
  }

  async getEnrollmentByProduct(productId: string): Promise<Enrollment | null> {
    try {
      const { data } = await api.get(`/enrollments/my/product/${productId}`);
      return data.data || data || null;
    } catch {
      return null;
    }
  }

  // Admin methods
  async findAll(filters: EnrollmentFilters = {}): Promise<{
    data: Enrollment[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });

    const { data } = await api.get(`/enrollments?${params.toString()}`);
    return data;
  }

  async create(enrollment: {
    userId: string;
    productId?: string;
    accessGroupId?: string;
    expirationDate?: string;
  }): Promise<Enrollment> {
    const { data } = await api.post('/enrollments', enrollment);
    return data;
  }

  async cancel(id: string): Promise<Enrollment> {
    const { data } = await api.put(`/enrollments/${id}/cancel`);
    return data;
  }

  async extend(id: string, expirationDate: string): Promise<Enrollment> {
    const { data } = await api.put(`/enrollments/${id}/extend`, { expirationDate });
    return data;
  }
}

export const enrollmentsService = new EnrollmentsService();
